import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const PasswordStrengthCalculator = () => {
  const [password, setPassword] = useState('');
  const [strength, setStrength] = useState(null);
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);
  const [solutionSteps, setSolutionSteps] = useState([]);

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setError('');
  };

  const evaluatePasswordStrength = () => {
    if (!password) {
      setError('Please enter a password.');
      return;
    }

    let score = 0;

    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    if (/[A-Z]/.test(password)) score += 1;
    if (/[a-z]/.test(password)) score += 1;
    if (/[0-9]/.test(password)) score += 1;
    if (/[^A-Za-z0-9]/.test(password)) score += 1;

    const strengthLevels = ['Very Weak', 'Weak', 'Moderate', 'Strong', 'Very Strong'];
    const strengthLevel = strengthLevels[Math.min(score, strengthLevels.length - 1)];

    setStrength(strengthLevel);

    // Update chart data
    setChartData({
      labels: ['Password Strength'],
      datasets: [
        {
          label: 'Strength Level',
          data: [score],
          backgroundColor: ['rgba(75, 192, 192, 0.5)'],
          borderColor: ['rgba(75, 192, 192, 1)'],
          borderWidth: 1
        }
      ]
    });

    // Update solution steps
    setSolutionSteps([
      `Password: ${password}`,
      `Length: ${password.length}`,
      `Contains Uppercase: ${/[A-Z]/.test(password)}`,
      `Contains Lowercase: ${/[a-z]/.test(password)}`,
      `Contains Numbers: ${/[0-9]/.test(password)}`,
      `Contains Special Characters: ${/[^A-Za-z0-9]/.test(password)}`,
      `Strength Level: ${strengthLevel}`
    ]);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    evaluatePasswordStrength();
  };

  return (
    <>
      <Helmet>
        <title>Password Strength Calculator | BioLab Tools</title>
        <meta name="description" content="Evaluate the strength of your password with ease. Use this calculator to determine if your password is strong enough." />
        <meta name="keywords" content="password strength calculator, evaluate password, password security, strong password" />
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          <header className="mb-8 text-center">
            <div className="text-4xl font-bold text-green-600 mb-2">🔍</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">Password Strength Calculator</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Evaluate the strength of your password with ease. Determine if your password is strong enough.
            </p>
          </header>

          <div className="bg-white p-6 rounded-lg shadow-md max-w-2xl mx-auto">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={handlePasswordChange}
                  className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter password"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-green-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-green-700 transition duration-200"
              >
                Evaluate Password Strength
              </button>

              {error && (
                <div className="bg-red-100 text-red-700 p-4 rounded-md mt-4">
                  {error}
                </div>
              )}

              {strength !== null && (
                <div className="mt-8">
                  <h2 className="text-2xl font-bold text-gray-800 mb-6">Result</h2>
                  <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                    <p className="text-lg font-medium">Password Strength: {strength}</p>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Visualization</h3>
                    <div className="h-96">
                      <Bar
                        data={chartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          scales: {
                            y: {
                              beginAtZero: true,
                              title: { display: true, text: 'Strength Score' }
                            },
                            x: { title: { display: true, text: 'Category' } }
                          },
                          plugins: { title: { display: true, text: 'Password Strength Evaluation' } }
                        }}
                      />
                    </div>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-xl font-bold text-gray-800 mb-4">Solution Steps</h3>
                    <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
                      <ul className="list-disc pl-6 space-y-2">
                        {solutionSteps.map((step, index) => (
                          <li key={index} className="text-gray-600">{step}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Frequently Asked Questions (FAQ)</h2>
            <div className="space-y-6">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">What is password strength evaluation?</h3>
                <p className="text-gray-600">
                  Password strength evaluation is the process of determining how secure a password is based on various criteria such as length, complexity, and character variety.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">How does this calculator work?</h3>
                <p className="text-gray-600">
                  This calculator evaluates the strength of a password based on its length, use of uppercase and lowercase letters, numbers, and special characters.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Can I use this calculator for security purposes?</h3>
                <p className="text-gray-600">
                  Yes, this calculator is accurate and suitable for security purposes, helping you determine if your password is strong enough.
                </p>
              </div>
            </div>
          </div>

          {/* How to Use Section */}
          <div className="mt-16 bg-white p-6 rounded-lg shadow-md max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">How to Use the Password Strength Calculator</h2>
            <div className="space-y-8">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 1: Enter Password</h3>
                <p className="text-gray-600">
                  Input the password you want to evaluate.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 2: Evaluate Password Strength</h3>
                <p className="text-gray-600">
                  Click the "Evaluate Password Strength" button to get the result.
                </p>
              </div>

              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold text-blue-600 mb-4">Step 3: View Result and Visualization</h3>
                <p className="text-gray-600">
                  The result will be displayed, along with a bar chart visualizing the password strength evaluation.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PasswordStrengthCalculator;